import React, { useRef, useState } from "react";
import { FiClock } from "react-icons/fi";
import ModelMediaContainer from "../../Media/ModelMediaContainer";

/** A media-library pick as ModelMediaContainer hands it over. */
export type MediaPick = { secure_url?: string; context?: unknown } | null | undefined;

const tooltipStyle: React.CSSProperties = {
  position: "absolute",
  bottom: "calc(100% + 6px)",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 20,
  background: "#fef9c3",
  border: "1px solid #fde047",
  borderRadius: 6,
  padding: "10px 12px",
  boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
  minWidth: 160,
  textAlign: "center",
};

type LockedBtnProps = {
  label: string;
  pendingImg?: string | null;
  action: React.ReactNode;
};

function LockedBtn({ label, pendingImg, action }: LockedBtnProps) {
  const [hovered, setHovered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = () => { if (timer.current) clearTimeout(timer.current); setHovered(true); };
  const hide = () => { timer.current = setTimeout(() => setHovered(false), 120); };

  return (
    <div style={{ position: "relative", display: "inline-flex" }} onMouseEnter={show} onMouseLeave={hide}>
      <span
        className="btn btn-file btn-round"
        style={{ background: "#fef9c3", border: "1px solid #fde047", color: "#713f12", cursor: "not-allowed", pointerEvents: "none" }}
      >
        {label}
      </span>
      {hovered && (
        <div onMouseEnter={show} onMouseLeave={hide} style={tooltipStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "#713f12", fontSize: "0.78rem", fontWeight: 600, marginBottom: pendingImg ? 8 : 0 }}>
            <FiClock size={13} />
            <span>Pending review</span>
          </div>
          {pendingImg && (
            <img src={pendingImg} alt="proposed" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 4, display: "block", margin: "0 auto 10px" }} />
          )}
          {action}
        </div>
      )}
    </div>
  );
}

export type ImageUploadViewProps = {
  imgSrc: string | null;
  imgObject: { data: string; imgName: string };
  labelText: string;
  uniqueId?: string;
  onRemove?: () => void;
  onFileChange: (file?: File) => void;
  onMediaSelect: (img: MediaPick) => void;
  pendingImg?: string | null;
};

const ImageUploadView = ({
  imgSrc,
  imgObject,
  labelText,
  uniqueId,
  onRemove,
  onFileChange,
  onMediaSelect,
  pendingImg,
}: ImageUploadViewProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const showRemove = onRemove && (imgObject.data !== "" || imgSrc !== null);

  return (
    <>
      <div>
        {imgSrc ? (
          <img src={imgSrc} alt="preview" width="300" height="300" className="rounded" />
        ) : (
          <span className="fa-stack fa-2x mt-3 mb-2">
            <i className="fas fa-circle fa-stack-2x" />
            <i className="fas fa-store fa-stack-1x fa-inverse" />
          </span>
        )}
      </div>

      <br />

      <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {pendingImg !== undefined ? (
          <>
            <LockedBtn
              label="Change"
              pendingImg={pendingImg}
              action={
                <label htmlFor={`Imgupload_${uniqueId}`} className="btn btn-sm btn-outline-danger btn-round mb-0" style={{ cursor: "pointer" }}>
                  Change
                </label>
              }
            />
            {showRemove && (
              <LockedBtn
                label="Remove"
                pendingImg={null}
                action={
                  <button className="btn btn-sm mb-0" onClick={onRemove}>Remove</button>
                }
              />
            )}
          </>
        ) : (
          <>
            <label
              htmlFor={`Imgupload_${uniqueId}`}
              className={"btn " + (labelText === "Upload Picture" ? "btn-outline-default " : "btn-outline-danger") + " btn-file btn-round"}
            >
              {labelText}
            </label>
            {showRemove && (
              <button className="btn" onClick={onRemove}>Remove</button>
            )}
          </>
        )}

        <input
          ref={fileRef}
          id={`Imgupload_${uniqueId}`}
          style={{ display: "none" }}
          type="file"
          name={`Imgupload_${uniqueId}`}
          accept=".jpg, .jpeg, .png"
          onChange={(e) => onFileChange(e.target.files?.[0])}
        />
      </div>

      <div className="form-group">
        <ModelMediaContainer selectedImg={onMediaSelect} />
      </div>
    </>
  );
};

export default ImageUploadView;
